class ProductImageZoom {
  constructor(el) {
    this.el = el;
    this.container = el.parentElement; 
    
    this.scale = 1;
    this.minScale = 1;
    this.maxScale = 3; 
    this.doubleTapScale = 1.8;
    
    this.translateX = 0;
    this.translateY = 0;

    this.lastTouchEnd = 0;
    this.initialDistance = null;
    this.initialScale = this.scale;
    
    this.panStartX = 0;
    this.panStartY = 0;
    this.initialTranslateX = 0;
    this.initialTranslateY = 0;
    this.isDragging = false;
    this.pinchCenterX = 0;
    this.pinchCenterY = 0;

    this.bindEvents();
    
    this.el.style.transition = '0.7s ease-out';
    this.el.style.transformOrigin = '0px 0px';
  }

  bindEvents() {
    this.el.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.el.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.el.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
  }

  getDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  getCenter(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  handleTouchStart(e) {
    if (this.scale > 1 || e.touches.length > 1) {
      e.stopPropagation(); 
    }

    if (e.touches.length === 1) {
      const now = new Date().getTime();
      const timeDiff = now - this.lastTouchEnd;
      if (timeDiff < 300 && timeDiff > 0) {
        e.preventDefault();
        this.toggleZoom(e.touches[0]);
        this.lastTouchEnd = 0;
        return;
      }
      this.lastTouchEnd = now;

      if (this.scale > 1) {
        this.isDragging = true;
        this.panStartX = e.touches[0].clientX;
        this.panStartY = e.touches[0].clientY;
        this.initialTranslateX = this.translateX;
        this.initialTranslateY = this.translateY;
        this.el.style.transition = 'none'; 
      }
      
    } else if (e.touches.length === 2) {
      e.preventDefault();
      this.isDragging = false;
      this.initialDistance = this.getDistance(e.touches);
      this.initialScale = this.scale;
      
      const center = this.getCenter(e.touches);
      const rect = this.el.getBoundingClientRect();
      
      this.pinchCenterX = (center.x - rect.left) / this.scale;
      this.pinchCenterY = (center.y - rect.top) / this.scale;

      this.el.style.transition = 'none';
    }
  }

  handleTouchMove(e) {
    if (this.scale > 1 || e.touches.length > 1) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (e.touches.length === 1 && this.isDragging) {
      const deltaX = e.touches[0].clientX - this.panStartX;
      const deltaY = e.touches[0].clientY - this.panStartY;
      
      this.translateX = this.initialTranslateX + deltaX;
      this.translateY = this.initialTranslateY + deltaY;
      
      this.applyBounds();
      this.updateTransform();
      
    } else if (e.touches.length === 2) {
      const currentDistance = this.getDistance(e.touches);
      const scaleChange = currentDistance / this.initialDistance;
      const newScale = Math.min(Math.max(this.initialScale * scaleChange, this.minScale), this.maxScale);
      
      this.translateX = this.translateX - this.pinchCenterX * (newScale - this.scale);
      this.translateY = this.translateY - this.pinchCenterY * (newScale - this.scale);
      
      this.scale = newScale;
      this.applyBounds();
      this.updateTransform();
    }
  }

  handleTouchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false;
      this.el.style.transition = '0.7s ease-out';
      if (this.scale <= 1) {
        this.reset();
      } else {
        this.applyBounds();
        this.updateTransform();
      }
    } else if (e.touches.length === 1) {
      // Transition from pinch back to pan
      this.isDragging = true;
      this.panStartX = e.touches[0].clientX;
      this.panStartY = e.touches[0].clientY;
      this.initialTranslateX = this.translateX;
      this.initialTranslateY = this.translateY;
    }
  }

  toggleZoom(touch) {
    this.el.style.transition = '0.7s ease-out';
    if (this.scale > 1) {
      this.reset();
    } else {
      this.scale = this.doubleTapScale;
      const rect = this.el.getBoundingClientRect();
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;
      
      this.translateX = -touchX * (this.scale - 1);
      this.translateY = -touchY * (this.scale - 1);
      
      this.applyBounds();
      this.updateTransform();
    }
  }

  reset() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.updateTransform();
  }

  applyBounds() {
    const rect = this.container.getBoundingClientRect();
    const elWidth = this.el.offsetWidth * this.scale;
    const elHeight = this.el.offsetHeight * this.scale;
    
    // Allow panning if scaled image is bigger than container
    const minTx = Math.min(0, rect.width - elWidth);
    const minTy = Math.min(0, rect.height - elHeight);
    
    if (this.translateX > 0) this.translateX = 0;
    if (this.translateX < minTx) this.translateX = minTx;
    
    if (this.translateY > 0) this.translateY = 0;
    if (this.translateY < minTy) this.translateY = minTy;
  }

  updateTransform() {
    this.el.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0px) scale3d(${this.scale}, ${this.scale}, 1)`;
  }
}

function initZoom() {
  if (window.innerWidth <= 1024) { 
    // Wait slightly to ensure images are loaded and classes are applied
    setTimeout(() => {
      const images = document.querySelectorAll('.product-image, .global-media-settings--no-shadow');
      images.forEach(img => {
        if (!img.dataset.zoomInitialized && img.tagName === 'IMG') {
          // new ProductImageZoom(img);
          img._zoomInstance = new ProductImageZoom(img);
          img.dataset.zoomInitialized = true;
        }
      });
    }, 500);
  }
}

document.addEventListener('DOMContentLoaded', initZoom);
document.addEventListener('shopify:section:load', initZoom);
window.addEventListener('load', initZoom);
